import React, { useEffect, useRef, useState } from "react";
import { Upload, CheckCircle2, AlertTriangle, Loader2, FileSpreadsheet, Save, Eye } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent } from "@/components/ui/dialog";

export default function Workbooks() {
  const [list, setList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [savingIntros, setSavingIntros] = useState(null);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef();

  const load = () => api.get("/admin/workbooks").then((r) => {
    setList(r.data);
    const d = {};
    r.data.forEach((w) => { d[w.version] = {}; w.components.forEach((c) => { d[w.version][c.name] = c.intro || ""; }); });
    setDrafts(d);
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const setIntro = (version, name, val) =>
    setDrafts((d) => ({ ...d, [version]: { ...d[version], [name]: val } }));

  const saveIntros = async (version) => {
    setSavingIntros(version);
    try {
      await api.patch(`/admin/workbooks/${version}/intros`, { intros: drafts[version] });
      toast.success(`Section intros saved for v${version}.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save intros."); }
    finally { setSavingIntros(null); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setErrors([]);
    const fd = new FormData(); fd.append("file", file);
    try {
      const res = await api.post("/admin/workbooks/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (res.data.success) {
        toast.success(`Workbook v${res.data.version} published (${res.data.question_count} questions).`);
        load();
      } else {
        setErrors(res.data.errors || ["Validation failed."]);
        toast.error("Workbook validation failed. See details below.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const activate = async (v) => {
    await api.post(`/admin/workbooks/${v}/activate`); toast.success(`Version ${v} activated.`); load();
  };

  return (
    <div data-testid="workbooks-page">
      <div className="overline mb-2">Assessment Definition</div>
      <h1 className="font-serif text-4xl text-oxford tracking-tight">Workbooks</h1>
      <p className="text-[var(--text2)] mt-2 max-w-2xl">
        Upload a new Excel workbook to publish a new versioned assessment definition. Historical submissions
        always retain the version they were taken on.
      </p>

      <div className="bg-white border border-dashed border-[var(--brdr)] rounded-sm p-10 mt-8 text-center">
        <FileSpreadsheet className="h-10 w-10 text-gold mx-auto" strokeWidth={1.2} />
        <p className="mt-4 text-[var(--text2)] text-sm">Upload an .xlsx workbook. It will be validated before activation.</p>
        <input ref={fileRef} type="file" accept=".xlsx" onChange={onFile} className="hidden" data-testid="workbook-file-input" />
        <Button data-testid="upload-workbook-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
          className="btn-primary text-white h-11 px-7 mt-6">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="mr-2 h-4 w-4" /> Choose Workbook</>}
        </Button>
      </div>

      {errors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-sm p-6 mt-6" data-testid="validation-errors">
          <div className="flex items-center gap-2 text-[var(--danger)] font-medium mb-3">
            <AlertTriangle className="h-4 w-4" /> Validation Errors — workbook not activated
          </div>
          <ul className="space-y-1.5 text-sm text-[var(--danger)]">
            {errors.map((e, i) => <li key={i}>· {e}</li>)}
          </ul>
        </div>
      )}

      <div className="mt-8 space-y-4">
        {list.map((w) => (
          <div key={w.version} data-testid={`workbook-v${w.version}`}
            className="bg-white border border-[var(--brdr)] rounded-sm p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h3 className="font-serif text-xl text-oxford">Version {w.version}</h3>
                {w.active ? (
                  <span className="text-xs bg-green-50 text-[var(--success)] border border-green-200 rounded-full px-3 py-0.5 flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" /> Active
                  </span>
                ) : (
                  <button data-testid={`activate-v${w.version}`} onClick={() => activate(w.version)}
                    className="text-xs text-[var(--text2)] underline underline-offset-2 hover:text-oxford">Activate</button>
                )}
              </div>
              <div className="text-xs text-[var(--text2)] font-mono">{w.uploaded_at?.slice(0, 10)}</div>
            </div>
            <p className="text-sm text-[var(--text2)] mt-1">{w.filename}</p>
            <p className="text-xs text-[var(--text2)] mt-1">{w.component_count} components · {w.question_count} questions · by {w.uploaded_by}</p>

            <div className="mt-5 border-t border-[var(--brdr)] pt-5">
              <div className="overline mb-3">Section Introductions</div>
              <div className="space-y-4">
                {w.components.map((c) => (
                  <div key={c.name} data-testid={`intro-editor-${w.version}-${c.name}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-sm font-medium text-oxford">
                        {c.name}{c.is_legacy ? " · Legacy" : ""}
                        <span className="font-mono text-xs text-[var(--text2)] ml-2">w{c.weight} · {c.question_count}q</span>
                      </label>
                      <button data-testid={`preview-intro-${w.version}-${c.name}`}
                        onClick={() => setPreview({ name: c.name, isLegacy: c.is_legacy, text: drafts[w.version]?.[c.name] ?? "" })}
                        className="text-xs text-[var(--text2)] hover:text-oxford flex items-center gap-1">
                        <Eye className="h-3.5 w-3.5" /> Preview
                      </button>
                    </div>
                    <Textarea
                      data-testid={`intro-input-${w.version}-${c.name}`}
                      value={drafts[w.version]?.[c.name] ?? ""}
                      onChange={(e) => setIntro(w.version, c.name, e.target.value)}
                      placeholder={`Shown above the ${c.name} questions. Leave blank to use the default intro.`}
                      className="bg-white border-[var(--brdr)] min-h-[72px] text-sm resize-y" />
                  </div>
                ))}
              </div>
              <Button data-testid={`save-intros-${w.version}`} onClick={() => saveIntros(w.version)}
                disabled={savingIntros === w.version} className="btn-primary text-white h-10 px-6 mt-5">
                {savingIntros === w.version ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-2 h-4 w-4" /> Save section intros</>}
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-2xl p-0 overflow-hidden border-[var(--brdr)]" data-testid="intro-preview-dialog">
          <div className="bg-alabaster px-4 py-2 border-b border-[var(--brdr)] flex items-center gap-2">
            <Eye className="h-3.5 w-3.5 text-[var(--text2)]" />
            <span className="overline">Respondent Preview</span>
          </div>
          {preview && (
            <div className="text-center px-8 py-14">
              <div className="overline text-gold mb-4">{preview.isLegacy ? "Legacy Dimension" : "Component"}</div>
              <h2 className="font-serif text-3xl md:text-4xl text-oxford tracking-tight">{preview.name}</h2>
              <p className="mt-5 text-[var(--text2)] max-w-xl mx-auto leading-relaxed">
                {preview.text?.trim()
                  ? preview.text
                  : `The following questions explore your organization's ${preview.name.toLowerCase()}. Choose the statement that best reflects your business today.`}
              </p>
              <div className="mt-8 inline-flex items-center gap-2 bg-oxford text-white h-11 px-7 rounded-sm text-sm font-semibold opacity-90">
                Continue
              </div>
              {!preview.text?.trim() && (
                <p className="mt-6 text-xs text-[var(--text2)] italic">Showing the default intro (this field is empty).</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
